"use client";

import type { CSSProperties } from "react";

import Link from "next/link";
import { AlertTriangle, TrendingDown, HelpCircle, XCircle, ArrowRight } from "lucide-react";

export default function Problem() {
  return (
    <section className="py-24 bg-[rgb(var(--bg-section))] relative overflow-hidden" id="problem">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] rounded-full bg-[#3C2447]/8 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--text-primary))] mb-4">
              Das eigentliche Problem
            </h2>
            <p className="text-lg text-[rgb(var(--text-muted))]">
              Viele Unternehmen haben eine Website. Doch in der Praxis:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#3C2447]/20 hover:border-[#3C2447]/40 rounded-xl p-6 transition-all" data-testid="card-problem-1">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3C2447]/30 to-[rgb(var(--bg-surface))] flex items-center justify-center mb-4 border border-[#3C2447]/30">
                <TrendingDown className="w-5 h-5 text-[#7A5A8A]" />
              </div>
              <h3 className="font-semibold text-[rgb(var(--text-primary))] mb-3">Unregelmäßige Anfragen</h3>
              <ul
                className="card-list text-sm text-[rgb(var(--text-muted-strong))]"
                style={{ "--bullet-color": "rgb(122, 90, 138)" } as CSSProperties}
              >
                <li>Anfragen kommen ohne Plan – der Betrieb fehlt.</li>
                <li>Technische Fehler ziehen Rankings und Lead-Flow runter.</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#C9B37E]/15 hover:border-[#C9B37E]/30 rounded-xl p-6 transition-all" data-testid="card-problem-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9B37E]/20 to-[rgb(var(--bg-surface))] flex items-center justify-center mb-4 border border-[#C9B37E]/20">
                <AlertTriangle className="w-5 h-5 text-[#C9B37E]" />
              </div>
              <h3 className="font-semibold text-[rgb(var(--text-primary))] mb-3">Schwankende Platzierungen</h3>
              <ul
                className="card-list text-sm text-[rgb(var(--text-muted-strong))]"
                style={{ "--bullet-color": "rgb(201, 179, 126)" } as CSSProperties}
              >
                <li>Google reagiert auf jede technische Schwäche.</li>
                <li>Wenn nichts überwacht wird, fällt das Ranking unerwartet.</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#1B3A60]/20 hover:border-[#1B3A60]/40 rounded-xl p-6 transition-all" data-testid="card-problem-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B3A60]/30 to-[rgb(var(--bg-surface))] flex items-center justify-center mb-4 border border-[#1B3A60]/30">
                <HelpCircle className="w-5 h-5 text-[#4A7AB8]" />
              </div>
              <h3 className="font-semibold text-[rgb(var(--text-primary))] mb-3">Verantwortung liegt woanders</h3>
              <ul
                className="card-list text-sm text-[rgb(var(--text-muted-strong))]"
                style={{ "--bullet-color": "rgb(74, 122, 184)" } as CSSProperties}
              >
                <li>Erfolg hängt von Zufall, nicht vom System ab.</li>
                <li>Technik ohne Betrieb ist ein stilles Risiko.</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-surface-strong))] border border-[#1B3A60]/20 text-[rgb(var(--text-primary))] rounded-2xl p-8 md:p-12">
            <p className="text-lg md:text-xl leading-relaxed mb-6 text-[rgb(var(--text-muted-strong))]">
              Das liegt selten am Angebot. Und fast nie daran, dass „eine Website fehlt".
            </p>
            <div className="bg-gradient-to-r from-[#1B3A60]/10 to-[#3C2447]/10 border border-[#1B3A60]/20 rounded-xl p-6">
              <p className="text-[rgb(var(--text-primary))] font-semibold text-lg mb-2">Das Problem ist meist technisch:</p>
              <p className="text-[rgb(var(--text-muted))]">
                Websites sind nicht als dauerhafte Anfragen-Systeme entwickelt, sondern als einmalige Projekte.
              </p>
            </div>
          </div>

          <div className="mt-16">
            <h3 className="text-2xl md:text-3xl font-bold text-[rgb(var(--text-primary))] mb-8 text-center">
              Warum klassische Lösungen nicht ausreichen
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-[rgb(var(--text-muted))] mb-6">Der Markt bietet überwiegend:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-[#7A5A8A] mt-0.5 flex-shrink-0" />
                    <span className="text-[rgb(var(--text-muted-strong))]">Projektarbeit durch klassische Anbieter</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-[#7A5A8A] mt-0.5 flex-shrink-0" />
                    <span className="text-[rgb(var(--text-muted-strong))]">Günstige Tools zum Selbermachen</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-surface-strong))] border border-[#1B3A60]/15 rounded-xl p-6">
                <p className="text-[rgb(var(--text-primary)/0.8)] mb-4">Diese Modelle haben eines gemeinsam:</p>
                <ul className="space-y-3 text-[rgb(var(--text-muted))]">
                  <li>• Sie liefern eine Website</li>
                  <li>• Übernehmen aber keine Verantwortung dafür,</li>
                  <li>• Ob sie langfristig sichtbar und stabil bleibt</li>
                </ul>
                <div className="mt-6 pt-6 border-t border-[#1B3A60]/20">
                  <p className="text-[rgb(var(--text-primary)/0.8)] font-medium">
                    Sobald die Platzierung bei Google fällt, Seiten langsamer werden oder Formulare nicht mehr funktionieren, liegt das Risiko beim Unternehmen selbst.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <p className="text-[rgb(var(--text-muted-strong))] mb-3">
              Die ausführliche Analyse, warum WordPress- und Baukasten-Seiten Anfragen verlieren, steht auf der Unterseite.
            </p>
            <Link
              href="/warum-wordpress-keine-leads-bringt"
              className="inline-flex items-center justify-center gap-2 text-[#C9B37E] font-semibold hover:text-[#E8D4A8] transition-colors"
            >
              Zur WordPress-Analyse
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
